import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { api, streamRequest } from '../api/client'
import type { StreamEvent } from '../api/client'
import type { ReactNode } from 'react'

export interface Conversation {
  id: string
  title: string | null
  agent_id: string | null
  is_archived: boolean
  /** "schedule" = recurring rotina; "manual" = one-shot user Run; null = free chat / legacy */
  trigger: string | null
  seen_at: string | null
  /** First line of the most recent message, decrypted server-side. */
  last_message_snippet: string | null
  /** Timestamp of the most recent message (ISO). Used to render "09:32" etc. */
  last_message_at: string | null
  created_at: string
}

export interface StreamState {
  userDisplay: string              // what to show as the user message (with attachment preview)
  assistantContent: string          // accumulating assistant text
  isStreaming: boolean              // stream still open
  error: string | null              // last error message, if any
}

interface ConversationContextValue {
  conversations: Conversation[]
  archivedConversations: Conversation[]
  /** Legacy: all agent convs with seen_at null (for callers not yet migrated). */
  pendingAgentConversations: Conversation[]
  /** Legacy: everything that is not a pending agent conv. */
  normalConversations: Conversation[]
  /** New: only scheduled recurring runs (trigger='schedule'). Rendered under "Rotinas". */
  rotinaConversations: Conversation[]
  /** New: free chats + manual runs. Rendered under "Chats". */
  chatConversations: Conversation[]
  activeConvId: string | null
  setActiveConvId: (id: string | null) => void
  markAgentConvSeen: (id: string) => void
  loadConversations: () => Promise<void>
  loadArchived: () => Promise<void>
  deleteConversation: (id: string) => Promise<void>
  addConversation: (conv: Conversation) => void
  updateConversation: (id: string, patch: { title?: string; is_archived?: boolean }) => Promise<void>
  // Streaming — lives in context so it survives route changes
  streamStates: Map<string, StreamState>
  sendMessage: (convId: string, userDisplay: string, apiContent: string) => Promise<void>
  abortStream: (convId: string) => void
  clearStreamState: (convId: string) => void
}

const ConversationContext = createContext<ConversationContextValue | null>(null)

export function ConversationProvider({ children }: { children: ReactNode }) {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [archivedRaw, setArchivedRaw] = useState<Conversation[]>([])
  const [activeConvId, setActiveConvId] = useState<string | null>(null)
  const [streamStates, setStreamStates] = useState<Map<string, StreamState>>(new Map())
  const abortControllers = useRef<Map<string, AbortController>>(new Map())
  const location = useLocation()

  const markAgentConvSeen = useCallback((id: string) => {
    // Optimistic update — set seen_at locally immediately, then persist in DB.
    const now = new Date().toISOString()
    setConversations(prev => prev.map(c => c.id === id ? { ...c, seen_at: now } : c))
    api.post(`/chat/conversations/${id}/mark-seen`).catch(() => {
      // Rollback on failure
      setConversations(prev => prev.map(c => c.id === id ? { ...c, seen_at: null } : c))
    })
  }, [])

  // Legacy split (kept for components that still read pending/normal until migrated).
  const pendingAgentConversations = useMemo(
    () => conversations.filter(c => c.agent_id && !c.seen_at),
    [conversations],
  )
  const normalConversations = useMemo(
    () => conversations.filter(c => !c.agent_id || c.seen_at),
    [conversations],
  )

  // Sidebar split — Rotinas vs Chats.
  //
  // Decision 2026-04-20 (revised): **any conversation attached to an agent**
  // is a rotina — regardless of whether it was triggered by the scheduler or
  // by the user clicking "Correr". Rationale: when the user sees "Morning
  // Briefing" in Chats just because they ran it manually, it reads as a bug,
  // not a feature. The `trigger` field is kept as metadata for traceability
  // (and was used in the old categorisation) but no longer drives the UI.
  //
  // Rotinas are grouped by agent_id — only the most recent visible per agent.
  // Older runs are auto-archived server-side; we still dedupe client-side as
  // a defence against race conditions mid-deploy.
  const rotinaConversations = useMemo(() => {
    const withAgent = conversations.filter(c => c.agent_id)
    const latestByAgent = new Map<string, Conversation>()
    for (const c of withAgent) {
      const existing = latestByAgent.get(c.agent_id!)
      if (!existing) {
        latestByAgent.set(c.agent_id!, c)
        continue
      }
      const a = c.last_message_at || c.created_at
      const b = existing.last_message_at || existing.created_at
      if (a > b) latestByAgent.set(c.agent_id!, c)
    }
    return Array.from(latestByAgent.values()).sort((a, b) => {
      const ta = a.last_message_at || a.created_at
      const tb = b.last_message_at || b.created_at
      return tb.localeCompare(ta)
    })
  }, [conversations])

  // Chats: only free conversations — no agent attached.
  const chatConversations = useMemo(
    () => conversations.filter(c => !c.agent_id),
    [conversations],
  )

  const loadConversations = useCallback(async () => {
    try {
      const convs = await api.get<Conversation[]>('/chat/conversations')
      setConversations(convs)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => { loadConversations() }, [loadConversations])

  useEffect(() => {
    if (location.pathname.startsWith('/chat')) {
      loadConversations()
    } else {
      setActiveConvId(null)
    }
  }, [location.pathname])

  // Live-refresh: três camadas que combinadas mantêm a sidebar
  // actualizada sem o user ter de fazer refresh manual.
  //
  //   (1) Visibility change — quando volta à aba/PWA, refetch.
  //   (2) Polling — enquanto a aba está visível, refetch a cada 60s.
  //       Pausa em background para não desperdiçar bateria.
  //   (3) SW push hook — quando chega uma notificação push (briefing,
  //       draft, post-meeting), o service worker faz `postMessage`
  //       a todos os clientes abertos com type='conversations:invalidate'
  //       e nós refetchamos. Caminho real-time.
  useEffect(() => {
    let interval: number | undefined

    const refresh = () => loadConversations()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }

    const onSwMessage = (ev: MessageEvent) => {
      if (ev.data?.type === 'conversations:invalidate') refresh()
    }

    const startPolling = () => {
      if (interval) return
      interval = window.setInterval(() => {
        if (document.visibilityState === 'visible') refresh()
      }, 60_000)
    }
    const stopPolling = () => {
      if (interval) {
        clearInterval(interval)
        interval = undefined
      }
    }

    document.addEventListener('visibilitychange', onVisibility)
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', onSwMessage)
    }
    if (document.visibilityState === 'visible') startPolling()

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage)
      }
      stopPolling()
    }
  }, [loadConversations])

  const loadArchived = useCallback(async () => {
    try {
      const convs = await api.get<Conversation[]>('/chat/conversations?archived=true')
      setArchivedRaw(convs)
    } catch { /* ignore */ }
  }, [])

  async function deleteConversation(id: string) {
    try {
      await api.delete(`/chat/conversations/${id}`)
      // Drop from BOTH active and archived lists. Archived deletions used
      // to stick until the next refresh/archive-toggle because only the
      // active list was filtered here.
      setConversations(prev => prev.filter(c => c.id !== id))
      setArchivedRaw(prev => prev.filter(c => c.id !== id))
      if (activeConvId === id) setActiveConvId(null)
    } catch { /* ignore */ }
  }

  function addConversation(conv: Conversation) {
    setConversations(prev => [conv, ...prev])
  }

  const updateStreamState = useCallback((convId: string, patch: Partial<StreamState>) => {
    setStreamStates(prev => {
      const next = new Map(prev)
      const current = next.get(convId)
      if (!current) return prev
      next.set(convId, { ...current, ...patch })
      return next
    })
  }, [])

  const clearStreamState = useCallback((convId: string) => {
    abortControllers.current.delete(convId)
    setStreamStates(prev => {
      if (!prev.has(convId)) return prev
      const next = new Map(prev)
      next.delete(convId)
      return next
    })
  }, [])

  const abortStream = useCallback((convId: string) => {
    const ctrl = abortControllers.current.get(convId)
    if (ctrl) ctrl.abort()
  }, [])

  const sendMessage = useCallback(async (convId: string, userDisplay: string, apiContent: string) => {
    const abortController = new AbortController()
    abortControllers.current.set(convId, abortController)

    setStreamStates(prev => {
      const next = new Map(prev)
      next.set(convId, { userDisplay, assistantContent: '', isStreaming: true, error: null })
      return next
    })

    let accumulated = ''
    try {
      await streamRequest<StreamEvent>(
        `/chat/conversations/${convId}/messages`,
        { content: apiContent },
        (event) => {
          if (event.type === 'text_delta') {
            accumulated += event.content
            updateStreamState(convId, { assistantContent: accumulated })
          } else if (event.type === 'tool_result' && event.name === 'enable_push_notifications') {
            // Chat-driven push subscribe: the backend can't subscribe on
            // the user's behalf (Web Push needs a browser-issued endpoint),
            // so the tool returns a confirmation and we trigger the actual
            // pushManager.subscribe() via a window event. AppShell listens
            // and dispatches usePushNotifications.subscribe(). Solves the
            // Android PWA gap where the Settings page is hard to reach.
            window.dispatchEvent(new CustomEvent('seguru:enable-push'))
          } else if (event.type === 'error') {
            updateStreamState(convId, { error: event.error || 'Erro desconhecido' })
          }
        },
        abortController.signal,
      )
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Erro desconhecido'
      updateStreamState(convId, { error: errMsg })
    } finally {
      updateStreamState(convId, { isStreaming: false })
      abortControllers.current.delete(convId)
      // Refresh sidebar so title/updated_at reflects the new exchange.
      loadConversations()
    }
  }, [loadConversations, updateStreamState])

  async function updateConversation(id: string, patch: { title?: string; is_archived?: boolean }) {
    const body: Record<string, unknown> = {}
    if (patch.title !== undefined) body.title = patch.title
    if (patch.is_archived !== undefined) body.is_archived = patch.is_archived
    try {
      const updated = await api.patch<Conversation>(`/chat/conversations/${id}`, body)
      if (patch.is_archived === true) {
        setConversations(prev => prev.filter(c => c.id !== id))
        setArchivedRaw(prev => [updated, ...prev])
        if (activeConvId === id) setActiveConvId(null)
      } else if (patch.is_archived === false) {
        setArchivedRaw(prev => prev.filter(c => c.id !== id))
        setConversations(prev => [updated, ...prev])
      } else {
        setConversations(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c))
        setArchivedRaw(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c))
      }
    } catch { /* ignore */ }
  }

  return (
    <ConversationContext.Provider value={{
      conversations,
      archivedConversations: archivedRaw,
      pendingAgentConversations,
      normalConversations,
      rotinaConversations,
      chatConversations,
      activeConvId,
      setActiveConvId,
      markAgentConvSeen,
      loadConversations,
      loadArchived,
      deleteConversation,
      addConversation,
      updateConversation,
      streamStates,
      sendMessage,
      abortStream,
      clearStreamState,
    }}>
      {children}
    </ConversationContext.Provider>
  )
}

export function useConversations() {
  const ctx = useContext(ConversationContext)
  if (!ctx) throw new Error('useConversations must be used within ConversationProvider')
  return ctx
}
